import { Grid, Typography, makeStyles } from "@material-ui/core";
import * as React from "react";
import { useTranslation } from "react-i18next";
import {
  IFreeTextResponse,
  ILabelGroupContent,
  ILabelGroupResponse,
  IOptionsContent,
  IOptionsResponse,
  QuestionContent,
  QuestionResponse,
  QuestionType
} from "src/common/types";
import ResponseComment from "../ResponseComment";
import ScaleResponse from "./ScaleResponse";

const useStyles = makeStyles({
  categoryText: {
    fontWeight: 600,
    lineHeight: "1.9rem",
    margin: "0 0 8px"
  },
  container: {
    display: "flex" as "flex",
    minHeight: 107,
    padding: "18px 24px",
    position: "relative" as "relative"
  },
  noReponse: {
    "@media print": {
      color: "#e4e3e3"
    },
    color: "#909090"
  }
});

interface IOwnProps {
  response: QuestionResponse | string | null;
  placeholder: string;
  type: QuestionType | "comment" | null;
  content: QuestionContent | null;
}

const ResponseContent = ({ response, type, content, placeholder }: IOwnProps) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const hasResponse = Boolean(response);
  const renderResponse = React.useMemo(() => {
    if (hasResponse) {
      switch (type) {
        case QuestionType.NumericScale:
        case QuestionType.TextScale:
        case QuestionType.Scale: {
          const thisResponse = response as IOptionsResponse;
          const thisContent = content as IOptionsContent;
          return <ScaleResponse content={thisContent} value={thisResponse.value} type={type} />;
        }
        case QuestionType.RadioFreeText: {
          const thisResponse = response as ILabelGroupResponse;
          const thisContent = content as ILabelGroupContent;

          const checkedOption = thisContent.options.find((_, index) => index === thisResponse.category);

          return (
            <>
              {typeof thisResponse.category === "number" && checkedOption ? (
                <Typography className={classes.categoryText} color="primary">
                  {t(checkedOption.value)}
                </Typography>
              ) : null}
              <ResponseComment responseHTML={thisResponse.value || ""} />
            </>
          );
        }
        case QuestionType.LabelGroup: {
          const thisResponse = response as ILabelGroupResponse;
          const thisContent = content as ILabelGroupContent;

          const checkedOption = thisContent.options.find((_, index) => index === thisResponse.category);

          return (
            <>
              {typeof thisResponse.category === "number" && checkedOption ? (
                <Typography
                  className={classes.categoryText}
                  color={thisResponse.category === 1 ? "secondary" : "primary"}
                >
                  {t(checkedOption.value)}
                </Typography>
              ) : null}
              <ResponseComment responseHTML={thisResponse.value || ""} />
            </>
          );
        }
        case QuestionType.FreeText: {
          const thisResponse = response as IFreeTextResponse;
          return <ResponseComment responseHTML={thisResponse.value || ""} />;
        }
        case QuestionType.DropDown:
        case QuestionType.Radio: {
          const thisResponse = response as IOptionsResponse;
          const thisContent = content as IOptionsContent;

          const checkedOption = thisContent.options.find((_, index) => index === thisResponse.value);

          return checkedOption ? (
            <Typography className={classes.categoryText} color="primary">
              {t(checkedOption)}
            </Typography>
          ) : null;
        }
        case "comment": {
          const thisResponse = response as string;
          return <ResponseComment responseHTML={thisResponse || ""} />;
        }
        default:
          return null;
      }
    } else {
      return (
        <Typography className={classes.noReponse} align="center">
          {placeholder}
        </Typography>
      );
    }
  }, [type, response, content, placeholder, hasResponse]);

  return (
    <Grid className={classes.container} container alignItems={hasResponse ? "flex-start" : "center"}>
      <Grid item xs={12}>
        {renderResponse}
      </Grid>
    </Grid>
  );
};

export default React.memo(ResponseContent);
